// core/utils/utils.js
// Consolidated utility functions for the tarot carousel system

// function throttle(callback, limit) {
// 	var waiting = false;
// 	return function (...args) {
// 		if (!waiting) {
// 			callback.apply(this, args);
// 			waiting = true;
// 			setTimeout(function () {
// 				waiting = false;
// 			}, limit);
// 		}
// 	};
// }

// Selector for elements that own their own pointer/click behavior. Used by the
// focus guard (window-events) to avoid stealing focus on a control click.
const INTERACTIVE_SELECTOR =
	'input, select, textarea, button, a, label, [contenteditable=""], [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

// Narrow guard for the drag handler: elements that need the native pointer/caret
// and cannot coexist with pointer-capture + a movement threshold. Click-activated
// controls (a, button, label) are intentionally NOT here — in the deferred-
// preventDefault model a tap is still their click and a past-threshold drag still
// drags the carousel, so they need no guard.
const DRAG_IGNORE_SELECTOR =
	'input, select, textarea, [contenteditable=""], [contenteditable="true"], [data-tarot-no-drag]';

/**
 * debounce calls a function after a specified delay has passed since the last time it was invoked.
 * @param {Function} func - the function to debounce
 * @param {number} wait - the number of milliseconds to wait before calling func
 * @param {boolean} [immediate=false] - if true, func is called on the leading edge of the timeout
 * @returns {Function} a debounced function that delays invoking func
 */
function debounce(func, wait, immediate) {
	var timeout;
	var debounced = function (...args) {
		var context = this;
		var later = function () {
			timeout = null;
			if (!immediate) {
				func.apply(context, args);
			}
		};
		var callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) {
			func.apply(context, args);
		}
	};
	debounced.cancel = function () {
		clearTimeout(timeout);
		timeout = null;
	};
	return debounced;
}

/**
 * Deep merge two objects.
 * @param {Object} target - The target object.
 * @param {Object} source - The source object.
 * @returns {Object} - The merged object.
 */
function deepMerge(target, source) {
	const isObject = (obj) => obj && typeof obj === 'object';

	return Object.keys(source).reduce(
		(acc, key) => {
			if (Array.isArray(source[key])) {
				acc[key] = source[key];
			} else if (isObject(acc[key]) && isObject(source[key])) {
				acc[key] = deepMerge({ ...acc[key] }, source[key]);
			} else {
				acc[key] = source[key];
			}
			return acc;
		},
		{ ...target }
	);
}

// accepts number (int or float), Pixel value "10px", or percent "10%"
// returns a numerical value
function convertValueToNumber(value, width) {
	if (typeof value === 'number') {
		return value;
	}
	// must be a string to parse px/% values
	if (typeof value !== 'string') {
		return 0;
	}
	if (value.indexOf('px') > -1) {
		return parseFloat(value.replace('px', ''));
	} else if (value.indexOf('%') > -1) {
		return (parseFloat(value.replace('%', '')) / 100) * width;
	}
	return 0;
}


/**
 * Determines if looping is possible and should be enabled
 * @param {number} slideCount - Total number of slides
 * @param {Object} options - Carousel options containing loop and slidesPerView settings
 * @param {Object} [loopBuffer={left:0,right:0}] - Effect buffer requirements for extra slides
 * @returns {boolean} true if looping should be enabled, false otherwise
 */
function canLoop(slideCount, options, loopBuffer) {
	// early return for options
	if (options.loop == false) return false;

	const slidesPerView = options.slidesPerView || 1;
	const totalNeeded = Math.max(
		slidesPerView + loopBuffer.left + loopBuffer.right,
		slidesPerView + 1
	);

	return slideCount >= totalNeeded;
}

/**
 * Calculate which slides are visible in the viewport and their visibility percentages
 * @param {Object} ctx - shared module context containing store, emitter, etc.
 * @param {number} [trackPosition] - track position (if not provided, reads from store)
 * @param {number} [buffer=0] - additional buffer around viewport
 * @returns {Array} array of objects with slide info: {slide, index, visibilityPercent, isVisible, isFullyVisible}
 */
function getSlidesInViewport(ctx, trackPosition = null, buffer = 0) {
	// Get current data from the store
	const widths = ctx.store.getWidths();
	const slides = ctx.store.getSlides();
	const state = ctx.store.getState();

	// Use provided trackPosition or get from store
	if (trackPosition === null) {
		const animation = ctx.store.getAnimation();
		trackPosition = animation.trackPosition || 0;
	}

	const viewportWidth = widths.viewport;
	const slideWidth = widths.slide;
	const gapWidth = widths.gap;
	const slideAndGapWidth = widths.slideAndGap;

	// Visibility bounds: usually equal to viewport, but configurable via
	// options.visibilityBoundsElement so overflow-visible carousels can mark
	// spillover slides as visible (not inert/aria-hidden).
	const boundsWidth = widths.visibilityBoundsWidth || viewportWidth;
	const boundsOffset = widths.visibilityBoundsOffset || 0;

	// Convert track position to viewport bounds (used for parallax/effect math; with buffer)
	const viewportStart = -trackPosition - buffer;
	const viewportEnd = viewportStart + viewportWidth + buffer * 2;
	// Center of the actual visible viewport (not including buffer)
	const viewportCenter = -trackPosition + viewportWidth / 2;

	// Convert track position to visibility bounds (used for inert/aria-hidden/classes)
	const visStart = -trackPosition + boundsOffset - buffer;
	const visEnd = visStart + boundsWidth + buffer * 2;

	const slideInfo = [];

	for (let i = 0; i < slides.length; i++) {
		const slide = slides[i];
		const renderIndex = slide._renderIndex !== undefined ? slide._renderIndex : i;

		// Calculate slide bounds in the viewport
		const slideStart = renderIndex * slideAndGapWidth;
		const slideEnd = slideStart + slideWidth;

		// Viewport-based intersection (drives parallax/left/right visibility for effects)
		const viewportIntersectionStart = Math.max(slideStart, viewportStart);
		const viewportIntersectionEnd = Math.min(slideEnd, viewportEnd);
		const viewportIntersection = Math.max(0, viewportIntersectionEnd - viewportIntersectionStart);
		const viewportVisibilityPercent = Math.max(0, Math.min(1, viewportIntersection / slideWidth));

		// Bounds-based intersection (drives inert/aria-hidden/classes)
		const intersectionStart = Math.max(slideStart, visStart);
		const intersectionEnd = Math.min(slideEnd, visEnd);
		const intersection = Math.max(0, intersectionEnd - intersectionStart);

		const visibilityPercent = Math.max(0, Math.min(1, intersection / slideWidth));
		const isPartiallyVisible = visibilityPercent > 0;
		const isMostlyVisible = visibilityPercent >= 0.66;
		const isFullyVisible = visibilityPercent >= 0.98;

		// Calculate directional visibility for animations (viewport-based — effects care
		// about viewport-center geometry, not the wider visibility bounds)
		let leftVisibility = 0;
		let rightVisibility = 0;
		let parallax = 0;
		let parallaxVisibility = 1; // 1 is default

		if (viewportIntersection > 0) {
			// Calculate slide's center position relative to viewport center
			const slideCenter = slideStart + slideWidth / 2;
			const distanceFromCenter = slideCenter - viewportCenter;

			// Parallax: -1.0 (left) → 0.0 (center) → 1.0 (right)
			// Normalize by half viewport width for smooth transitions
			parallax = Math.max(-1, Math.min(1, distanceFromCenter / (viewportWidth / 2)));

			// Right visibility: 0.0 when entering from right → 1.0 when fully visible
			// Based on how far the slide has progressed into the viewport
			if (distanceFromCenter > 0) {
				// Slide is to the right of center - entering phase
				rightVisibility = Math.max(0, Math.min(1, viewportVisibilityPercent));
				// parallax visibility fade right
				parallaxVisibility = 1 - rightVisibility;
			} else {
				// Slide is at or past center - fully entered
				rightVisibility = 1.0;
			}

			// Left visibility: 0.0 when exiting to left → 1.0 when fully visible
			if (distanceFromCenter < 0) {
				// Slide is to the left of center - exiting phase
				leftVisibility = Math.max(0, Math.min(1, viewportVisibilityPercent));
				// parallax visibility fade left
				parallaxVisibility = (1 - leftVisibility) * -1;
			} else {
				// Slide is at or before center - not exiting yet
				leftVisibility = 1.0;
			}

			// When slide is fully visible in viewport, both should definitely be 1.0
			if (viewportVisibilityPercent >= 1.0) {
				leftVisibility = 1.0;
				rightVisibility = 1.0;
				parallaxVisibility = 0; // 0 when fully visible
			}
		} else {
			// Calculate slide's center position relative to viewport center
			const slideCenter = slideStart + slideWidth / 2;
			const distanceFromCenter = slideCenter - viewportCenter;

			// parallax visibility when hidden
			if (distanceFromCenter > 0) {
				parallaxVisibility = 1;
			}

			if (distanceFromCenter < 0) {
				parallaxVisibility = -1;
			}
		}

		slideInfo.push({
			slide,
			index: i,
			renderIndex,
			visibilityPercent,
			isPartiallyVisible,
			isMostlyVisible,
			isFullyVisible,
			leftVisibility,
			rightVisibility,
			parallax,
			parallaxVisibility,
			slideStart,
			slideEnd,
		});
	}

	return slideInfo;
}

/**
 * Round to nearest hundredth of a pixel to eliminate floating-point precision issues
 * without collapsing distinct positions together.
 * @param {number} value - The value to round
 * @returns {number} Rounded value
 */
function roundSubPixel(value) {
	return Math.round(value * 100) / 100;
}

/**
 * Resolves the visibilityBoundsElement option to an actual HTMLElement.
 * Falls back to viewportEl for unmatched selectors or invalid input.
 * @param {'viewport'|'carousel'|string|HTMLElement} value - the option value
 * @param {HTMLElement} carouselEl - the outer tarot-carousel element
 * @param {HTMLElement} viewportEl - the tarot-viewport element
 * @returns {HTMLElement} resolved bounds element
 */
function resolveVisibilityBoundsElement(value, carouselEl, viewportEl) {
	if (!value || value === 'viewport') return viewportEl;
	if (value === 'carousel') return carouselEl;
	if (value instanceof HTMLElement) return value;
	if (typeof value === 'string') {
		const resolved = carouselEl.closest(value);
		if (resolved) return resolved;
		console.warn(
			`[Tarot] visibilityBoundsElement selector "${value}" did not match any ancestor; falling back to viewport.`
		);
		return viewportEl;
	}
	return viewportEl;
}

// Create utils object containing all functions for ctx usage
const utils = Object.freeze({
	debounce,
	deepMerge,
	convertValueToNumber,
	canLoop,
	getSlidesInViewport,
	roundSubPixel,
	resolveVisibilityBoundsElement,
});

// Export individual functions for backward compatibility
// Export utils object for new ctx-based usage
export {
	debounce,
	deepMerge,
	convertValueToNumber,
	canLoop,
	getSlidesInViewport,
	roundSubPixel,
	resolveVisibilityBoundsElement,
	utils,
	INTERACTIVE_SELECTOR,
	DRAG_IGNORE_SELECTOR,
};
